import { Link, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Home,
  ShoppingBag,
  User,
  MapPin,
  CreditCard,
  Heart,
  Settings,
  LogOut,
  Leaf,
  HelpCircle,
  X,
  ScanEye,
  Gift,
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useSidebar } from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/Auth";
import Logo from "../../../assets/images/logo/logo.png";

export default function DashboardSidebar() {
  const { toggleSidebar } = useSidebar();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const mainNavigation = [
    { title: "Dashboard", to: "/dashboard/overview", icon: Home },
    { title: "Orders", to: "/dashboard/orders", icon: ShoppingBag },
    // { title: "Profile", to: "/dashboard/profile", icon: User },
    { title: "Addresses", to: "/dashboard/addresses", icon: MapPin },
    // { title: "Skin Analysis", to: "/dashboard/analysis/skin", icon: ScanEye },
    { title: "Transactions", to: "/dashboard/transactions", icon: CreditCard },
    { title: "Referrals", to: "/dashboard/referrals", icon: Gift },
    // { title: "Wishlist", to: "/dashboard/wishlist", icon: Heart },
  ];

  const handleLinkClick = () => {
    // Optionally guard this to only close on mobile
    if (window.innerWidth < 768) {
      toggleSidebar();
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/auth/login");
    } catch (error) {
      console.error("Logout failed", error);
    }
  };

  return (
    <Sidebar collapsible="icon" side="left">
      <SidebarHeader className="px-4 py-3">
        <div className="flex items-center justify-between w-full">
          <Link
            to="/dashboard"
            className="flex items-center gap-2"
            onClick={handleLinkClick}
          >
            <img
              src={Logo}
              alt="logo"
              style={{ width: "120px", height: "40px", align: "center" }}
            />
          </Link>

          <button
            className="md:hidden p-2 text-primary hover:text-secondary"
            onClick={toggleSidebar}
            aria-label="Close sidebar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </SidebarHeader>

      <Separator />

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="group-data-[state=collapsed]:hidden">
            Account
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNavigation.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild tooltip={item.title}>
                    <Link to={item.to} onClick={handleLinkClick}>
                      <item.icon />
                      <span className="group-data-[state=collapsed]:hidden">
                        {item.title}
                      </span>
                    </Link>
                  </SidebarMenuButton>
                  {item.badge && (
                    <Badge className="bg-secondary text-primary group-data-[state=collapsed]:hidden">
                      {item.badge}
                    </Badge>
                  )}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="group-data-[state=collapsed]:hidden">
            Support
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Help & Support">
                  <Link to="/dashboard/support" onClick={handleLinkClick}>
                    <HelpCircle />
                    <span className="group-data-[state=collapsed]:hidden">
                      Help & Support
                    </span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarSeparator />

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Settings">
              <Link to="/dashboard/settings" onClick={handleLinkClick}>
                <Settings />
                <span className="group-data-[state=collapsed]:hidden">
                  Settings
                </span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <SidebarMenuButton tooltip="Log Out">
                  <div className="flex items-center gap-2 cursor-pointer">
                    <LogOut />
                    <span className="group-data-[state=collapsed]:hidden">
                      Log Out
                    </span>
                  </div>
                </SidebarMenuButton>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    You will be logged out and redirected to the login page.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleLogout}>
                    Log Out
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
